import { useCallback, useState } from 'react';

import { api } from '@/lib/api';
import { fanficsCatalogApi, type FanficsCatalogType } from '@/lib/fanfics-catalog-api';
import { resolvePermanentUrl } from '@/features/seo-listing/utils/resolve-permanent-url';

interface Tag {
  id: number;
  title: string;
  titleHighlight: boolean;
  category: string;
  description: string;
  synonyms: string[];
  usageCount: number;
  isAdult?: boolean;
  is_adult?: boolean;
  isSpoiler: boolean;
  color: string | null;
  slug: string;
  slugLock: boolean;
  permanentUrl?: string | null;
}

interface TagSearchResult {
  tags: Tag[];
  hasNextPage: boolean;
  hasPrevPage: boolean;
  limit: number;
  nextPage: number | null;
  page: number;
  totalDocs: number;
  totalPages: number;
}

interface TagSearchCustomResult {
  tags: Tag[];
  hasNextPage: boolean;
  hasPrevPage: boolean;
  limit: number;
  nextPage: number | null;
  page: number;
  totalDocs: number;
  totalPages: number;
}

interface UseTagsOptions {
  useNewApi?: boolean;
  type?: FanficsCatalogType;
}

export function useTags(options: UseTagsOptions = {}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { useNewApi = false, type = 'fanfic' } = options;

  const searchTags = useCallback(
    async (query: string): Promise<{ id: string; name: string; permanentUrl?: string }[]> => {
      if (!query.trim()) return [];

      setLoading(true);
      setError(null);

      try {
        const encodedQuery = encodeURIComponent(query);
        const requestPath = `/api/tags-custom?where[title][like]=${encodedQuery}&limit=10${
          useNewApi ? `&type=${type}` : ''
        }`;
        const response = await (useNewApi ? fanficsCatalogApi : api).get<TagSearchResult>(
          requestPath,
        );

        const results = response.data.tags.map((tag) => ({
          id: tag.id.toString(),
          name: `${tag.title}${tag.is_adult || tag.isAdult ? ' 18+' : ''}`.trim(),
          permanentUrl: resolvePermanentUrl(tag.permanentUrl),
        }));

        return results;
      } catch (err) {
        console.error('Error searching tags:', err);
        setError(err instanceof Error ? err.message : 'Failed to search tags');
        return [];
      } finally {
        setLoading(false);
      }
    },
    [type, useNewApi],
  );

  const getTags = useCallback(
    async (
      page: number = 1,
      limit: number = 10,
      customQuery: string = '',
    ): Promise<TagSearchResult> => {
      setLoading(true);
      setError(null);

      try {
        const requestQuery = customQuery || `page=${page}&limit=${limit}`;
        const requestPath = `/api/tags-custom?${requestQuery}${
          useNewApi && !requestQuery.includes('type=') ? `&type=${type}` : ''
        }`;
        const response = await (useNewApi ? fanficsCatalogApi : api).get<TagSearchResult>(
          requestPath,
        );
        response.data.tags = response.data.tags.map((tag) =>
          Object.assign(tag, {
            name: `${tag.title}${tag.isAdult || tag.is_adult ? ' 18+' : ''}`.trim(),
          }),
        );
        return response.data;
      } catch (err) {
        console.error('Error fetching tags:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch tags');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [type, useNewApi],
  );

  const getTagsCustom = useCallback(
    async (
      page: number = 1,
      limit: number = 10,
      customQuery: string = '',
    ): Promise<TagSearchCustomResult> => {
      setLoading(true);
      setError(null);

      try {
        const requestQuery = customQuery || `page=${page}&limit=${limit}`;
        const requestPath = `/api/tags-custom?${requestQuery}${
          useNewApi && !requestQuery.includes('type=') ? `&type=${type}` : ''
        }`;
        const response = await (useNewApi ? fanficsCatalogApi : api).get<TagSearchCustomResult>(
          requestPath,
        );
        response.data.tags = response.data.tags.map((tag) =>
          Object.assign(tag, {
            name: `${tag.title}${tag.isAdult || tag.is_adult ? ' 18+' : ''}`.trim(),
          }),
        );
        return response.data;
      } catch (err) {
        console.error('Error fetching tags:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch tags');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [type, useNewApi],
  );

  return {
    searchTags,
    getTags,
    getTagsCustom,
    loading,
    error,
  };
}
